import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { apiClient } from "@/lib/api-client";
import { useToast } from "@/hooks/use-toast";
import { getApiErrorMessage } from "@/lib/errors";
import type {
    CreatePropertyRequest,
    UpdatePropertyRequest,
    PropertyLinkedContact,
    PropertyLinkedOrganization,
} from "@/api/generated/client/models";

// ===============================
// Property CRUD
// ===============================

export const PROPERTIES_PAGE_SIZE = 50;

export interface PropertiesFilters {
    search?: string;
    page?: number;
    limit?: number;
}

export function useProperties(filters: PropertiesFilters = {}) {
    const { search, page = 1, limit = PROPERTIES_PAGE_SIZE } = filters;
    const queryClient = useQueryClient();
    const { toast } = useToast();

    const offset = (page - 1) * limit;

    const { data: response, isLoading, isPlaceholderData } = useQuery({
        queryKey: ["properties", search, page, limit],
        queryFn: async () => {
            return await apiClient.properties.listProperties({
                search: search || undefined,
                limit,
                offset,
            });
        },
        placeholderData: keepPreviousData,
    });

    const properties = response?.properties || [];
    const total = response?.meta?.total ?? properties.length;
    const totalPages = Math.ceil(total / limit);

    const createMutation = useMutation({
        mutationFn: async (data: CreatePropertyRequest) => {
            return await apiClient.properties.createProperty({
                createPropertyRequest: data,
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["properties"] });
            toast({ title: "Property created", description: "The property has been added." });
        },
        onError: async (error: unknown) => {
            const message = await getApiErrorMessage(error);
            toast({ title: "Failed to create property", description: message, variant: "destructive" });
        },
    });

    const updateMutation = useMutation({
        mutationFn: async ({ id, data }: { id: number; data: UpdatePropertyRequest }) => {
            return await apiClient.properties.updateProperty({
                id,
                updatePropertyRequest: data,
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["properties"] });
            toast({ title: "Property updated" });
        },
        onError: async (error: unknown) => {
            const message = await getApiErrorMessage(error);
            toast({ title: "Failed to update property", description: message, variant: "destructive" });
        },
    });

    const deleteMutation = useMutation({
        mutationFn: async (id: number) => {
            return await apiClient.properties.deleteProperty({ id });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["properties"] });
            toast({ title: "Property deleted" });
        },
        onError: async (error: unknown) => {
            const message = await getApiErrorMessage(error);
            toast({ title: "Failed to delete property", description: message, variant: "destructive" });
        },
    });

    return {
        properties,
        total,
        totalPages,
        isLoading,
        isPlaceholderData,
        createProperty: createMutation.mutateAsync,
        updateProperty: updateMutation.mutateAsync,
        deleteProperty: deleteMutation.mutate,
        isCreating: createMutation.isPending,
        isUpdating: updateMutation.isPending,
        isDeleting: deleteMutation.isPending,
    };
}

// ===============================
// Property ↔ Contact
// ===============================

export function usePropertyContacts(propertyId: number | null) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const queryKey = ["property-contacts", propertyId];

  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: async () => {
      if (!propertyId) return { contacts: [] as PropertyLinkedContact[] };
      const res = await apiClient.properties.getPropertyContacts({ id: propertyId });
      return { contacts: res.contacts ?? [] };
    },
    enabled: !!propertyId,
  });

  const linkMutation = useMutation({
    mutationFn: async (contactId: number) => {
      if (propertyId == null) throw new Error("Cannot link contact: propertyId is not set");
      await apiClient.properties.linkPropertyContact({
        id: propertyId,
        linkPropertyContactRequest: { contactId },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      toast({ title: "Contact linked" });
    },
    onError: async (error: unknown) => {
      const message = await getApiErrorMessage(error);
      toast({ title: "Failed to link contact", description: message, variant: "destructive" });
    },
  });

  const unlinkMutation = useMutation({
    mutationFn: async (contactId: number) => {
      if (propertyId == null) throw new Error("Cannot unlink contact: propertyId is not set");
      await apiClient.properties.unlinkPropertyContact({
        id: propertyId,
        contactId,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      toast({ title: "Contact unlinked" });
    },
    onError: async (error: unknown) => {
      const message = await getApiErrorMessage(error);
      toast({ title: "Failed to unlink contact", description: message, variant: "destructive" });
    },
  });

  return {
    contacts: data?.contacts ?? [],
    isLoading,
    linkContact: linkMutation.mutateAsync,
    unlinkContact: unlinkMutation.mutate,
    isLinking: linkMutation.isPending,
  };
}

// ===============================
// Property ↔ Organization
// ===============================

export function usePropertyOrganizations(propertyId: number | null) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const queryKey = ["property-organizations", propertyId];

  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: async () => {
      if (!propertyId) return { organizations: [] as PropertyLinkedOrganization[] };
      const res = await apiClient.properties.getPropertyOrganizations({ id: propertyId });
      return { organizations: res.organizations ?? [] };
    },
    enabled: !!propertyId,
  });

  const linkMutation = useMutation({
    mutationFn: async (organizationId: number) => {
      if (propertyId == null) throw new Error("Cannot link organization: propertyId is not set");
      await apiClient.properties.linkPropertyOrganization({
        id: propertyId,
        linkPropertyOrganizationRequest: { organizationId },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      toast({ title: "Organization linked" });
    },
    onError: async (error: unknown) => {
      const message = await getApiErrorMessage(error);
      toast({ title: "Failed to link organization", description: message, variant: "destructive" });
    },
  });

  const unlinkMutation = useMutation({
    mutationFn: async (organizationId: number) => {
      if (propertyId == null) throw new Error("Cannot unlink organization: propertyId is not set");
      await apiClient.properties.unlinkPropertyOrganization({
        id: propertyId,
        organizationId,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      toast({ title: "Organization unlinked" });
    },
    onError: async (error: unknown) => {
      const message = await getApiErrorMessage(error);
      toast({ title: "Failed to unlink organization", description: message, variant: "destructive" });
    },
  });

  return {
    organizations: data?.organizations ?? [],
    isLoading,
    linkOrganization: linkMutation.mutateAsync,
    unlinkOrganization: unlinkMutation.mutate,
    isLinking: linkMutation.isPending,
  };
}

// ===============================
// Property ↔ Claims (reverse lookup)
// ===============================

export interface PropertyClaimLink {
  id: number;
  companyId: number;
  claimId: number;
  propertyId: number;
  addressType: string;
  insuredName?: string;
  claimNumber?: string;
  createdAt: string;
}

export function usePropertyClaims(propertyId: number | null) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { data, isLoading } = useQuery({
    queryKey: ["property-claims", propertyId],
    queryFn: async () => {
      if (!propertyId) return { claimProperties: [] as PropertyClaimLink[] };
      const res = await apiClient.properties.getPropertyClaims({ id: propertyId });
      return { claimProperties: res.claimProperties ?? [] };
    },
    enabled: !!propertyId,
  });

  const linkMutation = useMutation({
    mutationFn: async (claimId: number) => {
      if (propertyId == null) throw new Error("Cannot link claim: propertyId is not set");
      await apiClient.claims.linkClaimProperty({
        claimId,
        linkClaimPropertyRequest: { propertyId },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["property-claims", propertyId] });
      toast({ title: "Claim linked" });
    },
    onError: async (error: unknown) => {
      const message = await getApiErrorMessage(error);
      toast({ title: "Failed to link claim", description: message, variant: "destructive" });
    },
  });

  const unlinkMutation = useMutation({
    mutationFn: async (claimId: number) => {
      if (propertyId == null) throw new Error("Cannot unlink claim: propertyId is not set");
      await apiClient.claims.unlinkClaimProperty({ claimId, propertyId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["property-claims", propertyId] });
      toast({ title: "Claim unlinked" });
    },
    onError: async (error: unknown) => {
      const message = await getApiErrorMessage(error);
      toast({ title: "Failed to unlink claim", description: message, variant: "destructive" });
    },
  });

  return {
    propertyClaims: data?.claimProperties || [],
    isLoading,
    linkClaim: linkMutation.mutateAsync,
    unlinkClaim: unlinkMutation.mutate,
  };
}
